/* tslint:disable:prefer-for-of */
import * as React from 'react';
import {action, observable} from 'mobx';
import {ColorEnum} from '../enum/color.enum';
import {observer} from 'mobx-react';
import styled, {keyframes} from 'styled-components';
import {Label} from '@atlaskit/field-base';
import {Ellipsis} from './ellipsis';
import {CrossIcon, ErrorIcon, InvoiceIcon, PenIcon, PlusIcon, ReloadIcon, RubbishIcon} from './icons';
import {BlockColorEnum} from '../enum/block-color.enum';
import {AvailableFileTypes} from '../enum/block/available-file-types.enum';

export interface IFileInputProps {
    name?: string;
    uploadFileErrorMessagePrefix?: string;
    label: string;
    disabled?: boolean;
    labelColor?: string;
    noFileChosenMessage: string;
    replaceFileMessage: string;
    uploadMoreDocumentsMessage?: string;
    dropFileMessage: string;
    placeholder: string;
    multiple?: boolean;
    onChange: (file: File[]) => void;
    onCancel?: (file: File) => void;
    onReload?: (file: File) => void;
    required?: boolean;
    files: File[];
    fileProgress?: { complete: number, error: boolean, total: number }[];
    onBlur?: any;
    isInvalid?: boolean;
    availableTypes?: AvailableFileTypes[];
    fileSizeLimit?: number;
    maxFilesLimit?: number;
}

interface IFileItemContainerProps {
    isDragging: boolean;
    isFocused: boolean;
    isInvalid: boolean;
}

const FileItemContainer = styled.div`
    align-items: center;
    background-color: ${(props: IFileItemContainerProps) => props.isDragging ? BlockColorEnum.PrimaryBackgroundColor : BlockColorEnum.SecondaryTextColor};
    border-radius: 5px;
    display: flex;
    flex: 1 0 auto;
    font-size: 14px;
    justify-content: space-between;
    line-height: 1.4285714285714286;
    max-width: 100%;
    overflow: hidden;
    transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out;
    word-wrap: break-word;
    border: 2px dashed ${(props: IFileItemContainerProps) => {
        if (props.isInvalid) {
            return BlockColorEnum.ErrorColor;
        } else if (props.isFocused) {
            return BlockColorEnum.PrimaryButtonColor;
        } else if (props.isDragging) {
            return BlockColorEnum.PrimaryTextColor;
        } else {
            return BlockColorEnum.TertiaryBackgroundColor;
        }
    }};
    color: ${(props: IFileItemContainerProps) => props.isInvalid ? BlockColorEnum.ErrorColor : BlockColorEnum.TertiaryBackgroundColor};
    padding: 7px 7px;
    min-height: 34px;
    margin-top: 10px;
    position: relative;
    &:hover {
      border: 2px dashed ${BlockColorEnum.PrimaryTextColor};
      color: ${BlockColorEnum.PrimaryTextColor};
      & .progressIconsWrapper {
        color: ${BlockColorEnum.PrimaryTextColor} !important;
      }
    }
`;
const IconContainer = styled.div`
    width: 19px;
    display: flex;
`;
const TextContainer = styled.div`
    display: flex;
    flex: 1 1 calc(100% - 48px);
    margin-left: 10px;
    width: calc(100% - 48px);
`;

const FileInputWrapper = styled.div`
  position: relative;
  & label div {
     color: ${(props: { labelColor?: string }) => props.labelColor ? props.labelColor : BlockColorEnum.PrimaryTextColor};
  }
`;

const FileInputControl = styled.input`
  opacity: 0.0;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: ${(props: { fullWidth: boolean, error: boolean }) => props.fullWidth ? '100%' : (props.error ? 'calc(100% - 60px)' : 'calc(100% - 30px)')};
  height:100%;
`;

const FileNameContainer = (props: { fileName: string, error: boolean }) => (
    <>
        <IconContainer>
            {props.error ?
                <ErrorIcon
                    width={19}
                    height={19}
                /> :
                <InvoiceIcon
                    width={19}
                    height={19}
                />
            }
        </IconContainer>
        <TextContainer>
            <Ellipsis>
                {props.fileName}
            </Ellipsis>
        </TextContainer>
    </>
);

const FileProgressWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  ${(props: { error: boolean }) => props.error ? `color: ${BlockColorEnum.ErrorColor};` : ''}
`;

const FileProgressHolder = styled.div`
  width: calc(100% - ${(props: { error: boolean }) => props.error ? 60 : 30}px);
  height: 20px;
  border-radius: 10px;
  position: relative;
  background: ${(props: { error: boolean }) => props.error ? BlockColorEnum.ErrorColor : BlockColorEnum.InactiveColor};
`;

const progressAnimation = (from: number, to: number) => keyframes`
  0% {
    width: calc(${from * 100}% - 4px);
  }
  20% {
    width: calc(${(from + (to - from) * .2) * 100}% - 4px);
  }
  30% {
    width: calc(${(from + (to - from) * .2) * 100}% - 4px);
  }
  50% {
    width: calc(${(from + (to - from) * .5) * 100}% - 4px);
  }
  60% {
    width: calc(${(from + (to - from) * .5) * 100}% - 4px);
  }
  80% {
    width: calc(${(from + (to - from) * .8) * 100}% - 4px);
  }
  90% {
    width: calc(${(from + (to - from) * .8) * 100}% - 4px);
  }
  100% {
    width: calc(${to * 100}% - 4px);
  }
`;

interface IFileProgressIndicatorProps {
    progress: number;
    nextProgress: number;
}

const FileProgressIndicator = styled.div`
  width: calc(${(props: IFileProgressIndicatorProps) => props.nextProgress * 100}% - 4px);
  height: 16px;
  border-radius: 10px;
  position: absolute;
  left: 2px;
  top: 2px;
  background: ${BlockColorEnum.TertiaryBackgroundColor};
  transition: width 2s;
  animation: ${(props: IFileProgressIndicatorProps) => progressAnimation(props.progress, props.nextProgress)} 1.2s linear;
`;

const ProgressIconsWrapper = styled.div`
  color: ${BlockColorEnum.TertiaryBackgroundColor};
  display: flex;
`;

const FileProgress = (props: { progress: number, error: boolean, fileName: string, nextProgress: number, onCancel: () => void, onReload: () => void }) => (
    <FileProgressWrapper error={props.error}>
        {props.error ? <FileNameContainer fileName={props.fileName} error={props.error} /> :
            <FileProgressHolder error={props.error}>
                <FileProgressIndicator nextProgress={props.nextProgress} progress={props.progress}/>
            </FileProgressHolder>}
        <ProgressIconsWrapper className="progressIconsWrapper">
            {props.error && <ReloadIcon
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onReload();
                }}
            />}
            <CrossIcon
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onCancel();
                }}
            />
        </ProgressIconsWrapper>
    </FileProgressWrapper>
);

@observer
export class FileInput extends React.Component<IFileInputProps> {
    @(observable.shallow) files: File[] = [];
    @observable isDraggingFile: boolean = false;
    @observable focused: boolean = false;

    @action
    onChange = (files: FileList) => {
        if (!files || files.length === 0) {
            return;
        }
        if (this.props.multiple) {
            const maxFileSizeLimit = this.props.maxFilesLimit || 0;
            // noinspection TsLint
            for (let i = 0; i < files.length; i++) {
                if (i >= maxFileSizeLimit) {
                    break;
                }
                this.files.push(files[i]);
            }
        } else {
            this.files = [files[0]];
        }
        this.onChangeFiles();
    }

    @action
    removeFile = (index) => {
        this.files.splice(index, 1);
        this.onChangeFiles();
    }

    onChangeFiles = () => {
        if (this.props.onChange) {
            this.props.onChange(this.files.slice());
        }
    }

    @action
    showDragStyle = () => {
        if (this.props.disabled) {
            return;
        }
        this.isDraggingFile = true;
    }

    @action
    hideDragStyle = () => {
        this.isDraggingFile = false;
    }

    @action setStateFromProps = (props: IFileInputProps) => {
        this.files = props.files;
    }

    get canUploadMore() {
        const {multiple} = this.props;
        if (!multiple && this.files.length > 0) {
            return false;
        }
        const maxFileCountLimit = this.props.maxFilesLimit || 0;
        return !multiple || maxFileCountLimit === 0 || this.files.length < maxFileCountLimit;
    }

    get addAnotherPlaceholder() {
        const {placeholder} = this.props;
        if (!this.canUploadMore) {
            return null;
        }
        return (
            <FileItemContainer
                onDragOver={this.showDragStyle}
                onDragEnter={this.showDragStyle}
                onDragLeave={this.hideDragStyle}
                onDragEnd={this.hideDragStyle}
                onDrop={this.hideDragStyle}
                isFocused={this.focused}
                isInvalid={this.props.isInvalid}
                isDragging={this.isDraggingFile}
            >
                {this.isDraggingFile
                    ? <Ellipsis>{this.props.dropFileMessage}</Ellipsis>
                    : <>
                        <IconContainer>
                            <PlusIcon
                                width={19}
                                height={19}
                                style={{fill: ColorEnum.PearlGray}}
                            />
                        </IconContainer>
                        <TextContainer>
                            <Ellipsis>{placeholder}</Ellipsis>
                        </TextContainer>
                    </>}
                {this.getFileInputControl(1, false)}
            </FileItemContainer>
        );
    }

    @action
    focusField = () => this.focused = true

    @action
    deFocusField = () => {
        this.focused = false;
        if (this.props.onBlur) {
            this.props.onBlur();
        }
    }

    getFileInputControl(progress: number, error: boolean) {
        if (this.props.disabled) {
            return undefined;
        }
        const {multiple} = this.props;
        let title = this.props.noFileChosenMessage;
        if (this.props.multiple && this.files.length !== 0) {
            title = this.props.uploadMoreDocumentsMessage;
        }
        if (!this.props.multiple && this.files.length !== 0) {
            title = this.files[0].name;
        }
        return (
            <FileInputControl
                id={this.props.name}
                name={this.props.name}
                fullWidth={progress === 1}
                error={error}
                type="file"
                title={title}
                multiple={multiple}
                accept={this.props.availableTypes && this.props.availableTypes.map((fileType) => {
                    switch (fileType) {
                        case AvailableFileTypes.PDF:
                            return '.pdf,application/pdf';
                        case AvailableFileTypes.DOC:
                            return '.doc,application/msword';
                        case AvailableFileTypes.DOCX:
                            return '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                        case AvailableFileTypes.ICO:
                            return '.ico,image/ico ,image/icon ,text/ico,application/ico';
                    }
                }).join(',')}
                onChange={(e) => {
                    this.onChange(e.target.files);
                    e.target.value = '';
                }}
                onFocus={this.focusField}
                onBlur={this.deFocusField}
            />
        );
    }

    componentDidMount() {
        this.setStateFromProps(this.props);
    }

    componentWillReceiveProps(nextProps) {
        this.setStateFromProps(nextProps);
    }

    render() {
        const {label, labelColor} = this.props;
        const fileProgress = this.props.fileProgress || [];
        return (
            <FileInputWrapper labelColor={labelColor}>
                <Label
                    isRequired={this.props.required}
                    htmlFor={this.props.name}
                    label={label}
                />
                {this.addAnotherPlaceholder}
                {this.files.map((file, index) => {
                    const progressStatus: { complete: number, total: number, error: boolean } = fileProgress[index] !== undefined ? fileProgress[index] : {complete: 1, error: false, total: 1};
                    const progress = progressStatus.complete / progressStatus.total;
                    const nextProgress = (progressStatus.complete + 1) / progressStatus.total;
                    const isInvalid = this.props.isInvalid || progressStatus.error;
                    const fileName = `${progressStatus.error ? `${this.props.uploadFileErrorMessagePrefix} ` : ''}${file.name}`;
                    if (this.props.multiple) {
                        return <FileItemContainer
                            isInvalid={isInvalid}
                            isFocused={this.focused}
                            key={index}
                            title={fileName}
                        >
                            {progress === 1 ?
                                <>
                                    <FileNameContainer error={isInvalid} fileName={fileName}/>
                                    <IconContainer>
                                        <RubbishIcon
                                            onClick={() => this.removeFile(index)}
                                            width={19}
                                            height={19}
                                            style={{fill: ColorEnum.PearlGray}}
                                        />
                                    </IconContainer>
                                </> :
                                <FileProgress fileName={fileName} error={progressStatus.error} onCancel={() => this.props.onCancel && this.props.onCancel(file)} onReload={() => this.props.onReload && this.props.onReload(file)} nextProgress={nextProgress} progress={progress}/>}
                        </FileItemContainer>;
                    } else {
                        return <FileItemContainer
                            isInvalid={isInvalid}
                            isFocused={this.focused}
                            onDragOver={this.showDragStyle}
                            onDragEnter={this.showDragStyle}
                            onDragLeave={this.hideDragStyle}
                            onDragEnd={this.hideDragStyle}
                            onDrop={this.hideDragStyle}
                            isDragging={this.isDraggingFile}
                        >
                            {progress === 1 ?
                                <>
                                    {this.isDraggingFile ? <Ellipsis>{this.props.replaceFileMessage}</Ellipsis> :
                                        <FileNameContainer error={isInvalid} fileName={fileName}/>}
                                    <IconContainer>
                                        <PenIcon/>
                                    </IconContainer>
                                </> : <FileProgress fileName={fileName} error={progressStatus.error} onCancel={() => this.props.onCancel && this.props.onCancel(file)} onReload={() => this.props.onReload && this.props.onReload(file)} nextProgress={nextProgress} progress={progress}/>}
                            {this.getFileInputControl(progress, progressStatus.error)}
                        </FileItemContainer>;
                    }
                })}
            </FileInputWrapper>
        );
    }
}
